import { useCallback, useEffect, useState } from 'react';
import type { NextLink, Operation } from '@apollo/client';

export const CHECKOUT_VIEWER_SECRET_HEADER = 'X-Dutchie-Checkout-Secret';

const secretKey = (checkoutId: string): string => `checkoutViewerSecret:${checkoutId}`;

type CheckoutViewerSecretResolver = (checkoutId: string) => string | undefined;

const CHECKOUT_VIEWER_SECRET_RESOLVER = 'checkoutViewerSecretResolver';

export function checkoutViewerSecretContext(
  secret: string | undefined
): { headers: Record<string, string> } | undefined {
  return secret ? { headers: { [CHECKOUT_VIEWER_SECRET_HEADER]: secret } } : undefined;
}

export function checkoutViewerSecretResolverContext(
  resolveSecret: CheckoutViewerSecretResolver
): { [CHECKOUT_VIEWER_SECRET_RESOLVER]: CheckoutViewerSecretResolver } {
  return { [CHECKOUT_VIEWER_SECRET_RESOLVER]: resolveSecret };
}

export function setCheckoutViewerSecret(operation: Operation, forward: NextLink): ReturnType<NextLink> {
  const context = operation.getContext();
  const resolveSecret = context[CHECKOUT_VIEWER_SECRET_RESOLVER];
  const checkoutId = typeof operation.variables.checkoutId === 'string' ? operation.variables.checkoutId : '';
  const secret = typeof resolveSecret === 'function' ? resolveSecret(checkoutId) : undefined;

  if (secret) {
    operation.setContext({
      headers: {
        ...context.headers,
        [CHECKOUT_VIEWER_SECRET_HEADER]: secret,
      },
    });
  }

  return forward(operation);
}

export function getCheckoutViewerSecret(checkoutId: string): string | undefined {
  if (typeof window === 'undefined' || !checkoutId) {
    return undefined;
  }

  try {
    return window.localStorage.getItem(secretKey(checkoutId)) ?? undefined;
  } catch {
    return undefined;
  }
}

export function storeCheckoutViewerSecret(checkoutId: string, secret: string | null | undefined): void {
  if (typeof window === 'undefined' || !checkoutId || !secret) {
    return;
  }

  try {
    window.localStorage.setItem(secretKey(checkoutId), secret);
  } catch {
    // A storage failure must not prevent the checkout page from loading.
  }
}

export function clearCheckoutViewerSecret(checkoutId: string): void {
  if (typeof window === 'undefined' || !checkoutId) {
    return;
  }

  try {
    window.localStorage.removeItem(secretKey(checkoutId));
  } catch {
    // Clearing the credential is best-effort if storage is unavailable.
  }
}

type ViewerSecretState = { checkoutId: string; secret?: string };

export function useCheckoutViewerSecret(
  checkoutId: string
): {
  secret: string | undefined;
  ready: boolean;
  storeSecret: (secret: string) => void;
} {
  const [state, setState] = useState<ViewerSecretState>();

  useEffect(() => {
    setState({ checkoutId, secret: getCheckoutViewerSecret(checkoutId) });
  }, [checkoutId]);

  const storeSecret = useCallback(
    (secret: string) => {
      storeCheckoutViewerSecret(checkoutId, secret);
      setState({ checkoutId, secret });
    },
    [checkoutId]
  );

  return {
    secret: state?.checkoutId === checkoutId ? state.secret : undefined,
    ready: state?.checkoutId === checkoutId,
    storeSecret,
  };
}
